// =============================================================================
// Module Project - Repository: Attachments
// Upload, fetch, delete attachments (Supabase Storage + metadata table)
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { TaskAttachment } from '../types';

const BUCKET_NAME = 'project_attachments';

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ATTACHMENTS FOR TASK
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
  const { data, error } = await supabase
    .from('project_task_attachments')
    .select('*')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching attachments:', error);
    return [];
  }

  // Generate signed URLs for each attachment
  const attachments = await Promise.all(
    (data || []).map(async (row) => {
      const attachment: TaskAttachment = {
        id: row.id,
        org_id: row.org_id,
        project_id: row.project_id,
        task_id: row.task_id,
        file_name: row.file_name,
        mime_type: row.mime_type,
        file_size: row.file_size,
        storage_path: row.storage_path,
        uploaded_by: row.uploaded_by,
        created_at: row.created_at,
      };

      // Get signed URL (valid for 1 hour)
      try {
        const { data: urlData, error: urlError } = await supabase.storage
          .from(BUCKET_NAME)
          .createSignedUrl(row.storage_path, 3600);

        if (urlError) {
          console.warn('Error creating signed URL for:', row.file_name, urlError);
        }

        if (urlData?.signedUrl) {
          attachment.url = urlData.signedUrl;
        }
      } catch (e) {
        console.warn('Exception creating signed URL for:', row.file_name, e);
      }

      return attachment;
    })
  );

  return attachments;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPLOAD ATTACHMENT
// ─────────────────────────────────────────────────────────────────────────────
export async function uploadAttachment(
  orgId: string,
  projectId: string,
  taskId: string,
  file: File,
  uploadedBy?: string
): Promise<TaskAttachment | null> {
  // Generate unique storage path with sanitized file name
  const safeName = sanitizeFileName(file.name);
  const uniqueId = crypto.randomUUID();
  const storagePath = `${orgId}/${projectId}/${taskId}/${uniqueId}_${safeName}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw new Error(uploadError.message || 'Erreur lors du téléversement');
  }

  // Insert metadata
  const { data, error: insertError } = await supabase
    .from('project_task_attachments')
    .insert({
      org_id: orgId,
      project_id: projectId,
      task_id: taskId,
      file_name: file.name,
      mime_type: file.type || null,
      file_size: file.size,
      storage_path: storagePath,
      uploaded_by: uploadedBy || null,
    })
    .select()
    .single();

  if (insertError) {
    console.error('Error inserting attachment metadata:', insertError);
    // Cleanup uploaded file
    await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
    throw new Error(insertError.message || 'Erreur lors de l\'enregistrement des métadonnées');
  }

  // Get signed URL
  const { data: urlData } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(storagePath, 3600);

  return {
    id: data.id,
    org_id: data.org_id,
    project_id: data.project_id,
    task_id: data.task_id,
    file_name: data.file_name,
    mime_type: data.mime_type,
    file_size: data.file_size,
    storage_path: data.storage_path,
    uploaded_by: data.uploaded_by,
    created_at: data.created_at,
    url: urlData?.signedUrl,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE ATTACHMENT
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteAttachment(attachmentId: string): Promise<boolean> {
  // First get the storage path
  const { data: attachment, error: fetchError } = await supabase
    .from('project_task_attachments')
    .select('storage_path')
    .eq('id', attachmentId)
    .single();

  if (fetchError || !attachment) {
    console.error('Error fetching attachment for delete:', fetchError);
    return false;
  }

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([attachment.storage_path]);

  if (storageError) {
    console.error('Error deleting from storage:', storageError);
    // Continue to delete metadata anyway
  }

  // Delete metadata
  const { error: deleteError } = await supabase
    .from('project_task_attachments')
    .delete()
    .eq('id', attachmentId);

  if (deleteError) {
    console.error('Error deleting attachment metadata:', deleteError);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// GET DOWNLOAD URL
// ─────────────────────────────────────────────────────────────────────────────
export async function getDownloadUrl(storagePath: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(storagePath, 3600);

    if (error) {
      console.warn('Error creating signed URL:', error);
      return null;
    }

    return data?.signedUrl || null;
  } catch (e) {
    console.warn('Exception creating signed URL:', e);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DOWNLOAD FILE AS BLOB URL
// ─────────────────────────────────────────────────────────────────────────────
export async function downloadFileAsBlob(storagePath: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .download(storagePath);

    if (error || !data) {
      console.warn('Error downloading file:', error);
      return null;
    }

    // Create a blob URL
    return URL.createObjectURL(data);
  } catch (e) {
    console.warn('Exception downloading file:', e);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DOWNLOAD FILE AS RAW BLOB (for react-pdf)
// ─────────────────────────────────────────────────────────────────────────────
export async function downloadFileAsRawBlob(storagePath: string): Promise<Blob | null> {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .download(storagePath);

    if (error || !data) {
      console.warn('Error downloading file as blob:', error);
      return null;
    }

    return data;
  } catch (e) {
    console.warn('Exception downloading file as blob:', e);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FORMAT FILE SIZE
// ─────────────────────────────────────────────────────────────────────────────
export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─────────────────────────────────────────────────────────────────────────────
// SANITIZE FILE NAME
// Removes or replaces problematic characters (#, %, spaces, etc.)
// ─────────────────────────────────────────────────────────────────────────────
export function sanitizeFileName(fileName: string): string {
  // Get extension
  const lastDot = fileName.lastIndexOf('.');
  const name = lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
  const ext = lastDot > 0 ? fileName.slice(lastDot) : '';
  
  // Normalize unicode (decompose accents), strip diacritical marks, then clean
  const sanitized = name
    .normalize('NFD')                              // é → e + ́
    .replace(/[\u0300-\u036f]/g, '')               // Strip diacritical marks
    .replace(/[^a-zA-Z0-9._-]/g, '_')             // Keep only safe chars
    .replace(/_+/g, '_')                           // Collapse multiple underscores
    .replace(/^_|_$/g, '');                        // Trim underscores
  
  return (sanitized || 'file') + ext;
}
